import type { MenuItemData } from '@/components/admin/MenuBuilder.vue'
import { API_BASE_URL } from '@/utils/config'

export interface MenuLookupPage {
  id: string
  title: string
  path: string
  slug?: string
  pageId?: number
}

export interface MenuLookupCategory {
  id: number | string
  name: string
  slug?: string
  type?: 'post' | 'product' | 'tour'
}

export async function fetchMenuLookupCategories(
  siteStyle: string
): Promise<MenuLookupCategory[]> {
  const categories: MenuLookupCategory[] = []
  const loaders: Promise<void>[] = []

  if (siteStyle === 'magazine' || siteStyle === 'restaurant') {
    loaders.push(
      fetch(`${API_BASE_URL}/post_categories.php`).then(async (response) => {
        const data = await response.json()
        if (data.success && Array.isArray(data.data)) {
          categories.push(
            ...data.data.map((cat: { id: number | string; name: string; slug?: string }) => ({
              id: cat.id,
              name: cat.name,
              slug: cat.slug,
              type: 'post' as const,
            }))
          )
        }
      })
    )
  }

  if (siteStyle === 'eshop') {
    loaders.push(
      fetch(`${API_BASE_URL}/categories.php`).then(async (response) => {
        const data = await response.json()
        if (data.success && Array.isArray(data.data)) {
          categories.push(
            ...data.data.map((cat: { id: number | string; name: string; slug?: string }) => ({
              id: cat.id,
              name: cat.name,
              slug: cat.slug,
              type: 'product' as const,
            }))
          )
        }
      })
    )
  }

  if (siteStyle === 'tours') {
    loaders.push(
      fetch(`${API_BASE_URL}/tours.php?action=categories`).then(async (response) => {
        const data = await response.json()
        if (data.success && Array.isArray(data.data)) {
          categories.push(
            ...data.data.map((cat: { id: number | string; name: string; slug?: string }) => ({
              id: cat.id,
              name: cat.name,
              slug: cat.slug,
              type: 'tour' as const,
            }))
          )
        }
      })
    )
  }

  await Promise.all(loaders)
  return categories
}

function normalizeRef(referenceId?: string | number | null): string {
  return referenceId == null ? '' : String(referenceId)
}

export function findPageByReference(
  referenceId: string | number | undefined,
  pages: MenuLookupPage[]
): MenuLookupPage | undefined {
  const ref = normalizeRef(referenceId)
  if (!ref) return undefined

  return pages.find((page) => {
    if (page.id === ref) return true
    if (page.slug && page.slug === ref) return true
    if (page.pageId != null && String(page.pageId) === ref) return true
    if (ref.startsWith('page-') && page.id === ref) return true
    if (ref.startsWith('page-') && page.pageId != null && String(page.pageId) === ref.slice(5)) return true
    return false
  })
}

export function findCategoryByReference(
  referenceId: string | number | undefined,
  categories: MenuLookupCategory[]
): MenuLookupCategory | undefined {
  const ref = normalizeRef(referenceId)
  if (!ref) return undefined

  if (ref.startsWith('post-')) {
    const id = ref.slice(5)
    return categories.find((cat) => cat.type === 'post' && String(cat.id) === id)
  }

  if (ref.startsWith('product-')) {
    const id = ref.slice(8)
    return categories.find((cat) => cat.type === 'product' && String(cat.id) === id)
  }

  if (ref.startsWith('tour-')) {
    const id = ref.slice(5)
    return categories.find((cat) => cat.type === 'tour' && String(cat.id) === id)
  }

  return categories.find((cat) => String(cat.id) === ref || (cat.slug && cat.slug === ref))
}

export function resolveMenuItemLabel(
  item: MenuItemData,
  pages: MenuLookupPage[] = [],
  categories: MenuLookupCategory[] = []
): string {
  if (item.label?.trim()) return item.label.trim()

  switch (item.type) {
    case 'page': {
      const page = findPageByReference(item.referenceId, pages)
      return page?.title || item.url || 'Άτιτλο στοιχείου'
    }
    case 'category': {
      const category = findCategoryByReference(item.referenceId, categories)
      return category?.name || 'Κατηγορία'
    }
    case 'custom':
      return item.url || 'Σύνδεσμος'
    default:
      return item.type
  }
}

export function resolveMenuItemUrl(
  item: MenuItemData,
  pages: MenuLookupPage[] = [],
  categories: MenuLookupCategory[] = []
): string {
  if (item.type === 'custom') {
    return item.url || '#'
  }

  if (item.url?.trim()) {
    return item.url
  }

  switch (item.type) {
    case 'page': {
      const page = findPageByReference(item.referenceId, pages)
      if (page?.path) return page.path
      if (normalizeRef(item.referenceId) === 'home') return '/'
      const ref = normalizeRef(item.referenceId)
      if (ref.startsWith('/')) return ref
      return ref ? `/${ref}` : '#'
    }
    case 'category': {
      const ref = normalizeRef(item.referenceId)
      if (ref.startsWith('product-')) {
        const category = findCategoryByReference(ref, categories)
        return `/products?category=${category?.slug || ref.slice(8)}`
      }
      if (ref.startsWith('post-')) {
        return `/blog?category=${ref.slice(5)}`
      }
      if (ref.startsWith('tour-')) {
        const category = findCategoryByReference(ref, categories)
        return `/tours?category=${category?.slug || ref.slice(5)}`
      }
      const category = findCategoryByReference(item.referenceId, categories)
      if (category?.type === 'product') {
        return `/products?category=${category.slug || category.id}`
      }
      if (category?.type === 'tour') {
        return `/tours?category=${category.slug || category.id}`
      }
      if (category?.type === 'post') {
        return `/blog?category=${category.id}`
      }
      return `/blog?category=${ref}`
    }
    case 'post':
      return `/blog/${normalizeRef(item.referenceId)}`
    case 'tour':
      return `/tours/${normalizeRef(item.referenceId)}`
    case 'product':
      return `/product/${normalizeRef(item.referenceId)}`
    default:
      return '#'
  }
}

function normalizeMenuItemReference(
  item: MenuItemData,
  categories: MenuLookupCategory[]
): MenuItemData {
  const normalized = { ...item }
  const ref = normalizeRef(item.referenceId)

  if (item.type === 'category' && ref && !ref.includes('-')) {
    const category = categories.find((cat) => String(cat.id) === ref)
    if (category?.type === 'product') {
      normalized.referenceId = `product-${category.id}`
    } else if (category?.type === 'tour') {
      normalized.referenceId = `tour-${category.id}`
    } else if (category) {
      normalized.referenceId = `post-${category.id}`
    }
  }

  return normalized
}

export function enrichMenuItem(
  item: MenuItemData,
  pages: MenuLookupPage[],
  categories: MenuLookupCategory[]
): MenuItemData {
  const normalized = normalizeMenuItemReference(item, categories)
  const enriched: MenuItemData = {
    ...normalized,
    label: resolveMenuItemLabel(normalized, pages, categories),
    url: resolveMenuItemUrl(normalized, pages, categories),
    children: (item.children || []).map((child) => enrichMenuItem(child, pages, categories)),
  }
  return enriched
}

export function enrichMenuItems(
  items: MenuItemData[],
  pages: MenuLookupPage[],
  categories: MenuLookupCategory[]
): MenuItemData[] {
  return items.map((item) => enrichMenuItem(item, pages, categories))
}
